import type { TcDcaFormValues, StreamDcaParams } from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function validateNumberRange(
  value: number | null | undefined,
  min: number,
  max: number,
  fieldLabel: string,
  options?: { required?: boolean; integerOnly?: boolean; excludeMin?: boolean }
): string | undefined {
  if (value === null || value === undefined) {
    if (options?.required) return `${fieldLabel} is required`;
    return undefined;
  }
  const num = Number(value);
  if (Number.isNaN(num)) return `${fieldLabel} must be a number`;
  if (options?.integerOnly && !Number.isInteger(num)) {
    return `${fieldLabel} must be an integer`;
  }
  if (options?.excludeMin && num <= min) {
    return `${fieldLabel} must be greater than ${min}`;
  }
  if (!options?.excludeMin && num < min) {
    return `${fieldLabel} must be at least ${min}`;
  }
  if (num > max) return `${fieldLabel} must be at most ${max}`;
  return undefined;
}

function setFieldError(
  errors: Record<string, string>,
  path: string,
  message: string
): void {
  errors[path] = message;
}

// ---------------------------------------------------------------------------
// DCA parameter ranges
// ---------------------------------------------------------------------------

interface DiRanges {
  effective: [number, number];
  nominal: [number, number];
}

const DI_RANGES: DiRanges = {
  effective: [0, 100],
  nominal: [0, 100],
};

function getQiRange(): [number, number] {
  return [0, 100_000_000];
}

function getDiRange(diFormat: string): [number, number] {
  return diFormat === 'nominal' ? DI_RANGES.nominal : DI_RANGES.effective;
}

function getBFactorRange(): [number, number] {
  return [0, 10];
}

function getDlimRange(diFormat: string): [number, number] {
  return diFormat === 'nominal' ? DI_RANGES.nominal : DI_RANGES.effective;
}

// ---------------------------------------------------------------------------
// Per-stream validation
// ---------------------------------------------------------------------------

function validateStreamParams(
  params: StreamDcaParams,
  prefix: string,
  diFormat: string,
  errors: Record<string, string>
): void {
  // qi — not used by ratio
  if (params.dcaModel !== 'ratio') {
    const [qiMin, qiMax] = getQiRange();
    const qiErr = validateNumberRange(params.qi, qiMin, qiMax, 'qi');
    if (qiErr) setFieldError(errors, `${prefix}.qi`, qiErr);
  }

  // di — not used by ratio
  if (params.dcaModel !== 'ratio') {
    const [diMin, diMax] = getDiRange(diFormat);
    const diErr = validateNumberRange(params.di, diMin, diMax, 'di');
    if (diErr) setFieldError(errors, `${prefix}.di`, diErr);
  }

  // bFactor — only for hyperbolic/modified_hyperbolic
  if (
    params.dcaModel === 'hyperbolic' ||
    params.dcaModel === 'modified_hyperbolic'
  ) {
    const [bMin, bMax] = getBFactorRange();
    const bErr = validateNumberRange(params.bFactor, bMin, bMax, 'bFactor');
    if (bErr) setFieldError(errors, `${prefix}.bFactor`, bErr);
  }

  // dlim — only for modified_hyperbolic
  if (params.dcaModel === 'modified_hyperbolic') {
    const [dlimMin, dlimMax] = getDlimRange(diFormat);
    const dlimErr = validateNumberRange(params.dlim, dlimMin, dlimMax, 'dlim');
    if (dlimErr) setFieldError(errors, `${prefix}.dlim`, dlimErr);
  }

  // Ratio model params (ri, tElf, m) — required when model is ratio
  if (params.dcaModel === 'ratio') {
    const riErr = validateNumberRange(params.ri, 0.01, 10000, 'Ri', {
      required: true,
    });
    if (riErr) setFieldError(errors, `${prefix}.ri`, riErr);

    const tElfErr = validateNumberRange(params.tElf, 1, 10000, 't_elf', {
      integerOnly: true,
      required: true,
    });
    if (tElfErr) setFieldError(errors, `${prefix}.tElf`, tElfErr);

    const mErr = validateNumberRange(params.m, -1, 1, 'm', {
      required: true,
    });
    if (mErr) setFieldError(errors, `${prefix}.m`, mErr);
  }

  // Ramp-up params
  if (params.rampUpModel !== 'none') {
    const rampUpQiErr = validateNumberRange(
      params.rampUpQi,
      params.rampUpModel === 'linear' ? -100_000_000 : 0,
      100_000_000,
      'Ramp-up qi'
    );
    if (rampUpQiErr) setFieldError(errors, `${prefix}.rampUpQi`, rampUpQiErr);

    const rampUpDurationErr = validateNumberRange(
      params.rampUpDuration,
      0,
      100_000,
      'Ramp-up Duration',
      { integerOnly: true }
    );
    if (rampUpDurationErr)
      setFieldError(errors, `${prefix}.rampUpDuration`, rampUpDurationErr);

    if (
      params.rampUpModel === 'linear' ||
      params.rampUpModel === 'exponential'
    ) {
      const nMin = params.rampUpModel === 'linear' ? -1_000_000 : 0.0001;
      const nMax = params.rampUpModel === 'linear' ? 1_000_000 : 9.9999;
      const nErr = validateNumberRange(params.rampUpN, nMin, nMax, 'n');
      if (nErr) setFieldError(errors, `${prefix}.rampUpN`, nErr);
    }
  }
}

// ---------------------------------------------------------------------------
// Top-level validation (called by TanStack Form onChange)
// ---------------------------------------------------------------------------

export const onChangeValidation = (
  value: TcDcaFormValues
): { fields: Record<string, string>; form: string } | undefined => {
  const errors: Record<string, string> = {};

  // Forecast settings
  const durationErr = validateNumberRange(
    value.forecastDurationPeriod,
    1,
    100,
    'Forecast Duration',
    { required: true, integerOnly: true }
  );
  if (durationErr) setFieldError(errors, 'forecastDurationPeriod', durationErr);

  // Stream params
  validateStreamParams(
    value.oilDcaParams,
    'oilDcaParams',
    value.diFormat,
    errors
  );
  validateStreamParams(
    value.gasDcaParams,
    'gasDcaParams',
    value.diFormat,
    errors
  );
  validateStreamParams(
    value.waterDcaParams,
    'waterDcaParams',
    value.diFormat,
    errors
  );

  if (Object.keys(errors).length === 0) return undefined;

  // Build a summary of sections with errors for display
  const sections = new Set<string>();
  for (const key of Object.keys(errors)) {
    sections.add(key.split('.')[0]);
  }
  return { fields: errors, form: Array.from(sections).join(', ') };
};
